import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { stripeUrl } from "common/const";
import { EditPaymentMethodDialogue } from "components/settings/shared/billing-details/editPaymentDialogue";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { addPaymentMethod } from "data/mutations-common";
import { fetchPaymentMethod } from "data/queries-common";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";

export const PaymentMethodCard = () => {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const stripe = useStripe();
  const stripeElements = useElements();
  const [editingCC, setEditingCC] = useState(false);
  const queryClient = useQueryClient();

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  const paymentMethod = paymentMethodQuery.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Method</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ReactQueryErrorWrapper queries={[paymentMethodQuery]} />
        {paymentMethodQuery.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading payment method
          </p>
        )}
        {paymentMethodQuery.isSuccess && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              {paymentMethod ? (
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {toTitleCase(paymentMethod.method)} ending in{" "}
                    {paymentMethod.last4}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Expires {paymentMethod.expireMonth}/{paymentMethod.expireYear}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Add a credit card to start a trial or purchase a paid plan.
                </p>
              )}
              <Button variant="outline" onClick={() => setEditingCC(true)}>
                {paymentMethod ? "Change card" : "Add credit card"}
              </Button>
            </div>
            {paymentMethod?.expired && (
              <Alert variant="destructive">
                <TriangleAlert />
                <AlertDescription>Your credit card has expired</AlertDescription>
              </Alert>
            )}
            <EditPaymentMethodDialogue
              open={editingCC}
              handleOnClose={handleOnClose}
              stripeUrl={stripeUrl}
              paymentMethodMutation={paymentMethodMutation}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
